import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Award,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  MessageSquare,
  FileText,
  User,
  Lightbulb,
  CheckCircle,
  Video,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  RotateCcw,
  BarChart3,
  Users,
  Zap,
  ChevronUp,
  ChevronDown,
  Medal,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SessionFeedback() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [enhancedFeedback, setEnhancedFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoAvailable, setVideoAvailable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [sessionRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/sessions/${id}`),
        axios.get(`${API}/sessions/${id}/feedback`)
      ]);
      setSession(sessionRes.data);
      setFeedback(feedbackRes.data);
      
      // Check if video is available
      if (sessionRes.data.video_file) {
        setVideoAvailable(true);
      }
      
      // Fetch enhanced feedback with comparative analysis
      try {
        const enhancedRes = await axios.get(`${API}/sessions/${id}/feedback/enhanced`);
        setEnhancedFeedback(enhancedRes.data);
      } catch (err) {
        console.log("Enhanced feedback not available");
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Video controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const rect = e.target.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#059669]";
    if (score >= 60) return "text-[#2563EB]";
    return "text-[#D97706]";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-[#059669]";
    if (score >= 60) return "bg-[#2563EB]";
    return "bg-[#D97706]";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="text-center py-12">
        <p className="text-[#5C5C5C]">Feedback not available yet</p>
        <Link to="/dashboard">
          <Button className="mt-4">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const scoreBreakdown = [
    { label: "Content Clarity", value: feedback.content_clarity },
    { label: "Structure & Flow", value: feedback.structure_flow },
    { label: "Verbal Communication", value: feedback.verbal_communication },
    { label: "Confidence & Presence", value: feedback.confidence_presence },
    { label: "Engagement Level", value: feedback.engagement_level },
    { label: "Slide Effectiveness", value: feedback.slide_effectiveness },
    { label: "Q&A Handling", value: feedback.qa_handling }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Link to="/dashboard">
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Session Feedback
          </h1>
          <p className="text-[#5C5C5C] mt-1">{session?.topic_name}</p>
        </div>
      </div>

      {/* Overall Score Card */}
      <Card className="stats-card bg-primary text-white overflow-hidden">
        <CardContent className="py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div 
                className="w-32 h-32 rounded-full bg-white flex items-center justify-center"
                style={{ 
                  background: `conic-gradient(white ${feedback.overall_score * 3.6}deg, rgba(255,255,255,0.2) 0deg)`
                }}
              >
                <div className="w-28 h-28 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feedback.overall_score}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-white/70 text-sm uppercase tracking-wider">Overall Score</p>
                <p className="text-2xl font-bold mt-1">
                  {feedback.overall_score >= 80 ? "Excellent" : 
                   feedback.overall_score >= 60 ? "Good" : "Needs Improvement"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-bold">{feedback.strengths?.length || 0}</p>
                <p className="text-sm text-white/70">Strengths</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-bold">{feedback.improvements?.length || 0}</p>
                <p className="text-sm text-white/70">Areas to Improve</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Breakdown */}
      <Card className="stats-card" data-testid="score-breakdown">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Score Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            {scoreBreakdown.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#5C5C5C]">{item.label}</span>
                  <span className={`font-semibold ${getScoreColor(item.value)}`}>{item.value}</span>
                </div>
                <Progress value={item.value} className={`h-2 ${getScoreBg(item.value)}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Session Recording Video */}
      {videoAvailable && (
        <Card className="stats-card" data-testid="video-player">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              Session Recording
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative bg-black rounded-xl overflow-hidden">
              <video
                ref={videoRef}
                src={`${API}/sessions/${id}/video`}
                className="w-full aspect-video"
                onTimeUpdate={handleVideoProgress}
                onEnded={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />
              
              {/* Video Controls Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                {/* Progress Bar */}
                <div 
                  className="h-1 bg-white/30 rounded-full mb-3 cursor-pointer"
                  onClick={handleSeek}
                >
                  <div 
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${videoProgress}%` }}
                  />
                </div>
                
                {/* Control Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={togglePlay}
                      className="text-white hover:bg-white/20 rounded-full"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={restartVideo}
                      className="text-white hover:bg-white/20 rounded-full"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleMute}
                      className="text-white hover:bg-white/20 rounded-full"
                    >
                      {isMuted ? (
                        <VolumeX className="w-4 h-4" />
                      ) : (
                        <Volume2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleFullscreen}
                    className="text-white hover:bg-white/20 rounded-full"
                  >
                    <Maximize className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Play Button Overlay (shown when paused) */}
              {!isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                  onClick={togglePlay}
                >
                  <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                    <Play className="w-8 h-8 text-primary ml-1" />
                  </div>
                </div>
              )}
            </div>
            <p className="text-sm text-[#5C5C5C] mt-3 text-center">
              Review your presentation recording to see areas for improvement
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabs for detailed feedback */}
      <Card className="stats-card">
        <CardContent className="p-0">
          <Tabs defaultValue="strengths" className="w-full">
            <TabsList className="w-full rounded-none border-b bg-transparent p-0 grid grid-cols-4">
              <TabsTrigger 
                value="strengths" 
                data-testid="tab-strengths"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary py-4"
              >
                <Award className="w-4 h-4 mr-2" />
                Strengths
              </TabsTrigger>
              <TabsTrigger 
                value="improvements"
                data-testid="tab-improvements"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary py-4"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Improve
              </TabsTrigger>
              <TabsTrigger 
                value="presence"
                data-testid="tab-presence"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary py-4"
              >
                <User className="w-4 h-4 mr-2" />
                Presence
              </TabsTrigger>
              <TabsTrigger 
                value="plan"
                data-testid="tab-plan"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary py-4"
              >
                <Target className="w-4 h-4 mr-2" />
                Plan
              </TabsTrigger>
            </TabsList>

            <TabsContent value="strengths" className="p-6">
              <div className="space-y-3">
                {feedback.strengths?.map((strength, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-[#059669]/10 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
                    <p className="text-[#1A1A1A]">{strength}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="improvements" className="p-6">
              <div className="space-y-3">
                {feedback.improvements?.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-[#D97706]/10 rounded-xl">
                    <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                    <p className="text-[#1A1A1A]">{item}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="presence" className="p-6">
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-secondary/50 rounded-xl">
                    <p className="text-sm text-[#5C5C5C] mb-1">Confidence Level</p>
                    <p className="font-semibold text-[#1A1A1A] capitalize">
                      {feedback.presence_feedback?.confidence_level}
                    </p>
                  </div>
                  <div className="p-4 bg-secondary/50 rounded-xl">
                    <p className="text-sm text-[#5C5C5C] mb-1">Professional Presence</p>
                    <p className="font-semibold text-[#1A1A1A]">
                      {feedback.presence_feedback?.professional_presence}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-secondary/50 rounded-xl">
                  <p className="text-sm text-[#5C5C5C] mb-1">Facial Engagement</p>
                  <p className="text-[#1A1A1A]">{feedback.presence_feedback?.facial_engagement}</p>
                </div>
                {feedback.presence_feedback?.nervous_cues && (
                  <div className="p-4 bg-[#D97706]/10 rounded-xl">
                    <p className="text-sm text-[#5C5C5C] mb-1">Areas to Watch</p>
                    <p className="text-[#1A1A1A]">{feedback.presence_feedback?.nervous_cues}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="plan" className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Communication Habits to Improve
                  </h3>
                  <div className="space-y-2">
                    {feedback.improvement_plan?.communication_habits?.map((habit, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center">
                          {index + 1}
                        </span>
                        <p className="text-[#1A1A1A]">{habit}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-accent" />
                    Presentation Techniques to Practice
                  </h3>
                  <div className="space-y-2">
                    {feedback.improvement_plan?.presentation_techniques?.map((technique, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-accent/5 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-accent text-white text-sm flex items-center justify-center">
                          {index + 1}
                        </span>
                        <p className="text-[#1A1A1A]">{technique}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {feedback.improvement_plan?.focused_challenge && (
                  <div className="p-4 bg-primary rounded-xl text-white">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Your Challenge for Next Session
                    </h3>
                    <p>{feedback.improvement_plan?.focused_challenge}</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Enhanced Feedback: Comparative Analysis & Benchmarks */}
      {enhancedFeedback && (
        <>
          {/* Benchmarks & Comparison */}
          <Card className="stats-card" data-testid="benchmarks-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Performance Benchmarks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Your Score */}
                <div className="text-center p-4 bg-primary/10 rounded-xl">
                  <p className="text-4xl font-bold text-primary">{enhancedFeedback.benchmarks?.your_score || feedback.overall_score}</p>
                  <p className="text-sm text-[#5C5C5C] mt-1">Your Score</p>
                  <div className="mt-2 text-xs text-[#9CA3AF]">
                    Top {100 - (enhancedFeedback.benchmarks?.percentile || 50)}% performer
                  </div>
                </div>
                
                {/* Department Average */}
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <p className="text-3xl font-bold text-[#1A1A1A]">{enhancedFeedback.benchmarks?.department_avg || '-'}</p>
                  <p className="text-sm text-[#5C5C5C] mt-1">Dept. Average</p>
                  <div className={`mt-2 text-xs flex items-center justify-center gap-1 ${
                    (enhancedFeedback.benchmarks?.vs_department || 0) >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'
                  }`}>
                    {(enhancedFeedback.benchmarks?.vs_department || 0) >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {Math.abs(enhancedFeedback.benchmarks?.vs_department || 0)} pts
                  </div>
                </div>
                
                {/* Platform Average */}
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <p className="text-3xl font-bold text-[#1A1A1A]">{enhancedFeedback.benchmarks?.overall_avg || '-'}</p>
                  <p className="text-sm text-[#5C5C5C] mt-1">Platform Average</p>
                  <div className={`mt-2 text-xs flex items-center justify-center gap-1 ${
                    (enhancedFeedback.benchmarks?.vs_platform || 0) >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'
                  }`}>
                    {(enhancedFeedback.benchmarks?.vs_platform || 0) >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {Math.abs(enhancedFeedback.benchmarks?.vs_platform || 0)} pts
                  </div>
                </div>
                
                {/* Top Performers */}
                <div className="text-center p-4 bg-[#F59E0B]/10 rounded-xl">
                  <p className="text-3xl font-bold text-[#F59E0B]">{enhancedFeedback.benchmarks?.top_performer_avg || '-'}</p>
                  <p className="text-sm text-[#5C5C5C] mt-1">Top 10% Average</p>
                  <div className="mt-2 text-xs flex items-center justify-center gap-1 text-[#F59E0B]">
                    <Medal className="w-3 h-3" />
                    Target to reach
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Over Time */}
          {enhancedFeedback.session_history?.length > 0 && (
            <Card className="stats-card" data-testid="progress-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Your Progress Over Time
                  </span>
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    enhancedFeedback.comparative_analysis?.trend === 'improving' 
                      ? 'bg-[#059669]/10 text-[#059669]' 
                      : enhancedFeedback.comparative_analysis?.trend === 'declining'
                      ? 'bg-[#DC2626]/10 text-[#DC2626]'
                      : 'bg-gray-100 text-[#5C5C5C]'
                  }`}>
                    {enhancedFeedback.comparative_analysis?.trend === 'improving' && <TrendingUp className="w-3 h-3 inline mr-1" />}
                    {enhancedFeedback.comparative_analysis?.trend === 'declining' && <TrendingDown className="w-3 h-3 inline mr-1" />}
                    {enhancedFeedback.comparative_analysis?.score_change > 0 ? '+' : ''}{enhancedFeedback.comparative_analysis?.score_change} from avg
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[...enhancedFeedback.session_history].reverse().concat([{
                      topic: session?.topic_name,
                      score: feedback.overall_score,
                      date: session?.created_at
                    }])}>
                      <XAxis 
                        dataKey="topic" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value?.substring(0, 15) + (value?.length > 15 ? '...' : '')}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip 
                        formatter={(value, name) => [`${value}`, 'Score']}
                        labelFormatter={(label) => label}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#6366F1" 
                        strokeWidth={3}
                        dot={{ fill: '#6366F1', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 8, fill: '#6366F1' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personalized Improvement Plan */}
          {enhancedFeedback.personalized_plan && (
            <Card className="stats-card" data-testid="improvement-plan">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Personalized Improvement Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Focus Areas */}
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#D97706]" />
                    Priority Focus Areas
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {enhancedFeedback.personalized_plan.focus_areas?.map((area, idx) => (
                      <div key={idx} className={`p-4 rounded-xl ${
                        area.priority === 'high' ? 'bg-[#DC2626]/10 border border-[#DC2626]/20' : 'bg-[#F59E0B]/10 border border-[#F59E0B]/20'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-[#1A1A1A]">{area.area}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            area.priority === 'high' ? 'bg-[#DC2626] text-white' : 'bg-[#F59E0B] text-white'
                          }`}>
                            {area.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-[#5C5C5C]">{area.current_score}</span>
                          <span className="text-[#9CA3AF]">→</span>
                          <span className="text-[#059669] font-semibold">{area.target_score}</span>
                        </div>
                        <Progress value={area.current_score} className="h-1.5 mt-2" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Practice Exercises */}
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-[#2563EB]" />
                    Practice Exercises
                  </h3>
                  <div className="space-y-2">
                    {enhancedFeedback.personalized_plan.practice_exercises?.map((exercise, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-[#2563EB]/5 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium text-[#1A1A1A] text-sm">{exercise.area}</p>
                          <p className="text-[#5C5C5C] text-sm mt-0.5">{exercise.exercise}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weekly Goals */}
                <div className="p-4 bg-primary rounded-xl text-white">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    This Week&apos;s Goals
                  </h3>
                  <ul className="space-y-2">
                    {enhancedFeedback.personalized_plan.weekly_goals?.map((goal, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        {goal}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Q&A Feedback */}
      {feedback.qa_feedback && (
        <Card className="stats-card" data-testid="qa-feedback">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Q&A Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/50 rounded-xl">
                <p className="text-sm text-[#5C5C5C] mb-1">Clarity of Answers</p>
                <p className="text-[#1A1A1A]">{feedback.qa_feedback?.clarity_of_answers}</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-xl">
                <p className="text-sm text-[#5C5C5C] mb-1">Logical Flow</p>
                <p className="text-[#1A1A1A]">{feedback.qa_feedback?.logical_flow}</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-xl">
                <p className="text-sm text-[#5C5C5C] mb-1">Confidence Under Questioning</p>
                <p className="text-[#1A1A1A]">{feedback.qa_feedback?.confidence_under_questioning}</p>
              </div>
              {feedback.qa_feedback?.suggestions && (
                <div className="p-4 bg-primary/5 rounded-xl">
                  <p className="text-sm text-[#5C5C5C] mb-1">Suggestions</p>
                  <p className="text-[#1A1A1A]">{feedback.qa_feedback?.suggestions}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Back to Dashboard */}
      <div className="flex justify-center pt-4">
        <Link to="/dashboard">
          <Button 
            data-testid="back-to-dashboard"
            className="bg-primary hover:bg-primary/90 rounded-full px-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
}
